'use client'

import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import debounce from 'debounce'
import { type ChangeEvent, type ComponentPropsWithoutRef, type CSSProperties, type ForwardedRef, useCallback, useEffect, useRef } from 'react'
import { ownerWindow } from '../utils/ownerWindow'

function getStyleValue(value: string) {
  return parseInt(value, 10) || 0
}

const styles: {
  shadow: CSSProperties
} = {
  shadow: {
    // Visibility needed to hide the extra text area on iPads
    visibility: 'hidden',
    // Remove from the content flow
    position: 'absolute',
    // Ignore the scrollbar width
    overflow: 'hidden',
    height: 0,
    top: 0,
    left: 0,
    // Create a new layer, increase the isolation of the computed values
    transform: 'translateZ(0)',
  },
}

interface TextareaStyles {
  outerHeightStyle: number
  overflowing: boolean
}

function isObjectEmpty(object: TextareaStyles) {
  for (const _ in object) {
    return false
  }
  return true
}

function isEmpty(obj: TextareaStyles) {
  return isObjectEmpty(obj) || (obj.outerHeightStyle === 0 && !obj.overflowing)
}

export interface TextareaAutosizeProps extends Omit<ComponentPropsWithoutRef<'textarea'>, 'children' | 'rows'> {
  /**
   * textarea 元素的 ref
   */
  rootRef?: ForwardedRef<HTMLTextAreaElement>
  /**
   * 最多顯示列數
   */
  maxRows?: string | number
  /**
   * 最少顯示列數
   * @default 1
   */
  minRows?: string | number
}

/**
 * TextareaAutosize 可自動調整高度的多行文字輸入元件。
 * 根據內容自動伸縮，支援最小與最大列數限制。
 *
 * @example
 * ```tsx
 * <TextareaAutosize minRows={2} maxRows={6} placeholder="請輸入..." />
 * ```
 */
export const TextareaAutosize = function TextareaAutosize(props: TextareaAutosizeProps) {
  const { rootRef, onChange, maxRows, minRows = 1, style, value, ...rest } = props

  const { current: isControlled } = useRef(value != null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const handleRef = useMergeRefs(rootRef, textareaRef)
  const heightRef = useRef<number | null>(null)
  const hiddenTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  const calculateTextareaStyles = useCallback(() => {
    const textarea = textareaRef.current
    const hiddenTextarea = hiddenTextareaRef.current

    if (!textarea || !hiddenTextarea) {
      return undefined
    }

    const containerWindow = ownerWindow(textarea)
    const computedStyle = containerWindow.getComputedStyle(textarea)

    // If input's width is shrunk and it's not visible, don't sync height.
    if (computedStyle.width === '0px') {
      return {
        outerHeightStyle: 0,
        overflowing: false,
      }
    }

    hiddenTextarea.style.width = computedStyle.width
    hiddenTextarea.value = textarea.value || props.placeholder || 'x'
    if (hiddenTextarea.value.slice(-1) === '\n') {
      // Certain fonts which overflow the line height will cause the textarea
      // to report a different scrollHeight depending on whether the last line
      // is empty. Make it non-empty to avoid this issue.
      hiddenTextarea.value += ' '
    }

    const boxSizing = computedStyle.boxSizing
    const padding
      = getStyleValue(computedStyle.paddingBottom) + getStyleValue(computedStyle.paddingTop)
    const border
      = getStyleValue(computedStyle.borderBottomWidth) + getStyleValue(computedStyle.borderTopWidth)

    // The height of the inner content
    const innerHeight = hiddenTextarea.scrollHeight

    // Measure height of a textarea with a single row
    hiddenTextarea.value = 'x'
    const singleRowHeight = hiddenTextarea.scrollHeight

    // The height of the outer content
    let outerHeight = innerHeight

    if (minRows) {
      outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight)
    }
    if (maxRows) {
      outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight)
    }
    outerHeight = Math.max(outerHeight, singleRowHeight)

    // Take the box sizing into account for applying this value as a style.
    const outerHeightStyle = outerHeight + (boxSizing === 'border-box' ? padding + border : 0)
    const overflowing = Math.abs(outerHeight - innerHeight) <= 1

    return { outerHeightStyle, overflowing }
  }, [maxRows, minRows, props.placeholder])

  const didHeightChange = useCallbackEvent(() => {
    const textarea = textareaRef.current
    const textareaStyles = calculateTextareaStyles()

    if (!textarea || !textareaStyles || isEmpty(textareaStyles)) {
      return false
    }

    const outerHeightStyle = textareaStyles.outerHeightStyle

    return heightRef.current != null && heightRef.current !== outerHeightStyle
  })

  const syncHeight = useCallback(() => {
    const textarea = textareaRef.current
    const textareaStyles = calculateTextareaStyles()

    if (!textarea || !textareaStyles || isEmpty(textareaStyles)) {
      return
    }

    const outerHeightStyle = textareaStyles.outerHeightStyle
    if (heightRef.current !== outerHeightStyle) {
      heightRef.current = outerHeightStyle
      textarea.style.height = `${outerHeightStyle}px`
    }
    textarea.style.overflow = textareaStyles.overflowing ? 'hidden' : ''
  }, [calculateTextareaStyles])

  const frameRef = useRef(-1)

  useEffect(() => {
    const debouncedHandleResize = debounce(syncHeight)
    const textarea = textareaRef?.current

    if (!textarea) {
      return undefined
    }

    const containerWindow = ownerWindow(textarea)

    containerWindow.addEventListener('resize', debouncedHandleResize)

    let resizeObserver: ResizeObserver

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (didHeightChange()) {
          // avoid "ResizeObserver loop completed with undelivered notifications" error
          // by temporarily unobserving the textarea element while manipulating the height
          // and reobserving one frame later
          resizeObserver.unobserve(textarea)
          cancelAnimationFrame(frameRef.current)
          syncHeight()
          frameRef.current = requestAnimationFrame(() => {
            resizeObserver.observe(textarea)
          })
        }
      })
      resizeObserver.observe(textarea)
    }

    return () => {
      debouncedHandleResize.clear()
      cancelAnimationFrame(frameRef.current)
      containerWindow.removeEventListener('resize', debouncedHandleResize)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [calculateTextareaStyles, syncHeight, didHeightChange])

  useEffect(() => {
    syncHeight()
  })

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) {
      syncHeight()
    }

    const textarea = event.target
    const countOfCharacters = textarea.value.length
    const isLastCharacterNewLine = textarea.value.endsWith('\n')
    const isEndOfTheLine = textarea.selectionStart === countOfCharacters

    // Set the cursor position to the very end of the text.
    if (isLastCharacterNewLine && isEndOfTheLine) {
      textarea.setSelectionRange(countOfCharacters, countOfCharacters)
    }

    if (onChange) {
      onChange(event)
    }
  }

  return (
    <>
      <textarea
        value={value}
        onChange={handleChange}
        ref={handleRef}
        // Apply the rows prop to get a "correct" first SSR paint
        rows={minRows as number}
        style={style}
        {...rest}
      />
      <textarea
        aria-hidden
        className={props.className}
        readOnly
        ref={hiddenTextareaRef}
        tabIndex={-1}
        style={{
          ...styles.shadow,
          ...style,
          paddingTop: 0,
          paddingBottom: 0,
        }}
      />
    </>
  )
}
